import { CircleCheckIcon, LoaderCircleIcon } from "lucide-react";
import { format } from "date-fns";
import { Chip } from "@/components/badge/chip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DATE_TIME } from "@/lib/date";
import { cn } from "@/lib/utils";
import type { ExecStage, Prd } from "@/types/domain";

// PRD exec stage (WED-283), rendered as the server derived it: running spins
// a primary loader like the version page's in-process icon; done is static.
// The chip shows only the template's short name (icon carries the phase); the
// tooltip spells out the full stage label and the server's done rule so the
// stage isn't a black box.
const RULE = {
  running: "执行后 thread 还没有新帖 / PR 事件",
  done: "执行后 thread 已有新帖或 PR 事件",
} as const;

const SHORT_NAMES: Record<string, string> = {
  // implement is the old key of code
  implement: "Code",
  code: "Code",
  split: "Split",
  review: "Review",
  "code-review": "CR",
  "merge-deploy": "Deploy",
};

const shortName = (stage: ExecStage) =>
  Object.hasOwn(SHORT_NAMES, stage.template)
    ? SHORT_NAMES[stage.template]
    : stage.templateLabel;

// `fixed`: list rows reserve a fixed-width slot on the right (an empty one when
// there's no stage) so chips line up across rows; hidden on phones.
export const ExecStageChip = ({
  prd,
  className,
  fixed,
}: {
  prd: Pick<Prd, "execStage">;
  className?: string;
  fixed?: boolean;
}) => {
  const stage = prd.execStage;
  const slot = fixed && "hidden w-20 shrink-0 md:inline-flex";
  if (!stage) return slot ? <span aria-hidden="true" className={slot} /> : null;
  const running = stage.phase === "running";
  const Icon = running ? LoaderCircleIcon : CircleCheckIcon;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Chip
            tone={running ? "primary" : "green"}
            aria-label={stage.label}
            className={cn(
              "inline-flex items-center gap-1",
              slot && cn(slot, "justify-center"),
              className
            )}
          >
            <Icon aria-hidden="true" className={cn("h-3 w-3 shrink-0", running && "motion-safe:animate-spin")} />
            <span className="truncate">{shortName(stage)}</span>
          </Chip>
        </TooltipTrigger>
        <TooltipContent>
          <p>{stage.label}</p>
          <p>{RULE[stage.phase]}</p>
          <p>{format(new Date(stage.at), DATE_TIME)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
